import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Switch,
  Autocomplete,
  LinearProgress,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import { listCourses, listAllSubjects, listAllChapters, listAllTopics, apiErrorMessage } from "../../api/assessmentHierarchy";
import {
  listVideos,
  createVideo,
  updateVideo,
  deleteVideo,
  uploadVideoFile,
  readVideoDurationSeconds,
  formatDuration,
  resolveMediaUrl,
} from "../../api/assessmentVideos";
import { VIDEO_TYPES } from "./assessmentVideosData";
import useDebouncedValue from "./useDebouncedValue";
import useHierarchyCascade from "./useHierarchyCascade";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "title", label: "Name", cellCss: { align: "left", textAlign: "left", minWidth: "220px" }, getValue: (r) => r.title },
  { key: "subject", label: "Subject", cellCss: { minWidth: "150px" }, getValue: (r) => r.subjectNames.join(", ") || "—" },
  { key: "topic", label: "Topic", cellCss: { minWidth: "150px" }, getValue: (r) => r.topicName || "—" },
  { key: "category", label: "Category", cellCss: { minWidth: "110px" }, getValue: (r) => r.category || "—" },
  { key: "duration", label: "Duration", cellCss: { minWidth: "100px" }, getValue: (r) => formatDuration(r.durationSeconds) },
  { key: "recommended", label: "Recommended", cellCss: { align: "center", minWidth: "130px" }, getValue: (r) => r.recommended },
  { key: "displayOrder", label: "Display Order", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.displayOrder ?? "—" },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => (r.active ? "ACTIVE" : "INACTIVE") },
];

const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));
// Persists the HIDDEN columns (not the visible ones) so newly added columns show by default.
const STORAGE_KEY = "assessmentVideosHiddenColumns";

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        background: `linear-gradient(135deg, ${ACCENT}12 0%, ${ACCENT}03 100%)`,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
          boxShadow: `0 4px 10px ${ACCENT}40`,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e" }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>{subtitle}</Typography>}
      </Box>
    </Box>
  );
}
const MAX_VIDEO_MB = 200;

// Add (item = null, file required) and edit (item = row, file optional to replace) share this
// dialog. A video belongs to a Topic only, so the mapping cascades Course -> Subject -> Chapter -> Topic.
function VideoFormDialog({ open, item, hierarchy, onClose, onSave }) {
  const cascade = useHierarchyCascade({ ...hierarchy, subTopics: [] });
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [category, setCategory] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [recommended, setRecommended] = useState(false);
  const [active, setActive] = useState(true);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setTitle(item?.title || "");
    setTitleTouched(false);
    setCategory(item?.category || "");
    setDisplayOrder(item?.displayOrder ?? "");
    setRecommended(Boolean(item?.recommended));
    setActive(item ? Boolean(item.active) : true);
    setFile(null);
    setProgress(null);

    const topic = item ? hierarchy.topics.find((t) => t.id === item.topicId) : null;
    if (topic) {
      const chapter =
        hierarchy.chapters.find(
          (c) => topic.chapterIds.includes(c.id) && c.subjectIds.some((sid) => topic.subjectIds.includes(sid)),
        ) || hierarchy.chapters.find((c) => topic.chapterIds.includes(c.id));
      const subjectId = chapter?.subjectIds.find((sid) => topic.subjectIds.includes(sid)) ?? chapter?.subjectIds[0];
      const subject = hierarchy.subjects.find((s) => s.id === subjectId);
      cascade.setCourseId(subject?.courseIds?.[0] || "");
      cascade.setSubjectId(subjectId || "");
      cascade.setChapterId(chapter?.id || "");
      cascade.setTopicId(topic.id);
    } else {
      cascade.setCourseId("");
      cascade.setSubjectId("");
      cascade.setChapterId("");
      cascade.setTopicId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const handleFile = (e) => {
    const picked = e.target.files?.[0] || null;
    if (picked && picked.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error(`Video must not exceed ${MAX_VIDEO_MB} MB`);
      return;
    }
    setFile(picked);
    if (picked && !title.trim()) setTitle(picked.name.replace(/\.[^.]+$/, ""));
  };

  const isValid = title.trim().length > 0 && Boolean(cascade.topicId) && Boolean(item || file);

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: item?.id,
        title: title.trim(),
        topicId: cascade.topicId,
        category: category.trim() || undefined,
        displayOrder: displayOrder === "" ? undefined : Number(displayOrder),
        recommended,
        active,
        file,
        onProgress: setProgress,
      });
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const dropSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.75,
    p: 2,
    borderRadius: 2,
    border: "1.5px dashed",
    borderColor: file ? "#2e7d32" : `${ACCENT}66`,
    backgroundColor: file ? "#eaf7ec" : `${ACCENT}08`,
    cursor: "pointer",
    transition: "all 0.15s ease-in-out",
    "&:hover": { borderColor: file ? "#2e7d32" : ACCENT, backgroundColor: file ? "#e3f3e5" : `${ACCENT}12` },
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <Box sx={{ position: "relative", p: 3, background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`, color: "#fff" }}>
        <IconButton
          onClick={onClose}
          disabled={submitting}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            {item ? <OndemandVideoOutlinedIcon /> : <UploadFileIcon />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>{item ? "Edit Video" : "Upload Video"}</Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {item ? "Update video details and mapping" : "Pick a file, name it and map it to a topic"}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr" }, gap: 3, alignItems: "start" }}>
          <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
            <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box component="label" sx={dropSx}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: file ? "#2e7d32" : ACCENT,
                    color: "#fff",
                  }}
                >
                  {file ? <CheckCircleIcon fontSize="small" /> : <UploadFileIcon fontSize="small" />}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: "0.85rem", color: file ? "#2e7d32" : "#1a1a2e" }}>
                    {file ? file.name : item ? "Click to replace the video file (optional)" : "Click to choose a video file *"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                    {file
                      ? "Click to choose a different file"
                      : item?.videoPath
                        ? `Current: ${item.videoPath.split("/").pop()}`
                        : `Up to ${MAX_VIDEO_MB} MB`}
                  </Typography>
                </Box>
                <input type="file" hidden accept="video/*" onChange={handleFile} />
              </Box>
              {submitting && progress != null && (
                <Box>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.5 }}>Uploading… {progress}%</Typography>
                </Box>
              )}
              <TextField
                label="Name"
                fullWidth
                size="small"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => setTitleTouched(true)}
                error={titleTouched && !title.trim()}
                helperText={titleTouched && !title.trim() ? "Please fill out this field" : undefined}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Autocomplete
                freeSolo
                size="small"
                options={VIDEO_TYPES}
                inputValue={category}
                onInputChange={(_, value) => setCategory(value)}
                renderInput={(params) => <TextField {...params} label="Category" />}
              />
              <TextField
                label="Display Order (optional)"
                type="number"
                fullWidth
                size="small"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: recommended ? ACCENT : "divider",
                  backgroundColor: recommended ? `${ACCENT}0d` : "#f5f7fb",
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>Recommended</Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>Highlight this video for students</Typography>
                </Box>
                <Switch checked={recommended} onChange={(e) => setRecommended(e.target.checked)} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: active ? "#2e7d32" : "divider",
                  backgroundColor: active ? "#eaf7ec" : "#f5f7fb",
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: active ? "#2e7d32" : "#555" }}>
                    {active ? "Active" : "Inactive"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                    {active ? "Visible to students" : "Hidden from students"}
                  </Typography>
                </Box>
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2e7d32" },
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
            <SectionHeader
              icon={<AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Mapping"
              subtitle="Exam → Subject → Chapter → Topic"
            />
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Exam</InputLabel>
                  <Select label="Exam" value={cascade.courseId} onChange={(e) => cascade.setCourseId(e.target.value)}>
                    {hierarchy.courses.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Subject</InputLabel>
                  <Select label="Subject" value={cascade.subjectId} onChange={(e) => cascade.setSubjectId(e.target.value)}>
                    {cascade.subjectOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Chapter</InputLabel>
                  <Select label="Chapter" value={cascade.chapterId} onChange={(e) => cascade.setChapterId(e.target.value)}>
                    {cascade.chapterOptions.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Topic</InputLabel>
                  <Select label="Topic *" value={cascade.topicId} onChange={(e) => cascade.setTopicId(e.target.value)}>
                    {cascade.topicOptions.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: `linear-gradient(135deg, #123a80 0%, #0d47a1 100%)` },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {item ? "Save Changes" : "Upload"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function AssessmentVideos() {
  const [videos, setVideos] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [subjectFilter, setSubjectFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recommendedFilter, setRecommendedFilter] = useState("");

  const [orderBy, setOrderBy] = useState("title");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [watchItem, setWatchItem] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const hidden = JSON.parse(raw);
        if (Array.isArray(hidden)) {
          const visible = allColumns.map((c) => c.key).filter((k) => !hidden.includes(k));
          if (visible.length > 0) return new Set(visible);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const loadVideos = () => {
    setLoading(true);
    listVideos({
      page,
      size: rowsPerPage,
      search: debouncedSearch,
      subjectId: subjectFilter,
      topicId: topicFilter,
      isActive: statusFilter === "" ? undefined : statusFilter === "ACTIVE",
      recommended: recommendedFilter === "" ? undefined : recommendedFilter === "YES",
    })
      .then((data) => {
        setVideos(data.content);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => {
        setVideos([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load videos"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, subjectFilter, topicFilter, statusFilter, recommendedFilter]);

  useEffect(() => {
    Promise.all([listCourses(), listAllSubjects(), listAllChapters(), listAllTopics()])
      .then(([courseRows, subjectRows, chapterRows, topicRows]) => {
        setCourses(courseRows);
        setSubjects(subjectRows);
        setChapters(chapterRows);
        setTopics(topicRows);
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load content hierarchy")));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(allColumns.map((c) => c.key).filter((k) => !visibleColumns.has(k))),
      );
    } catch {}
  }, [visibleColumns]);

  const filterTopicOptions = useMemo(
    () => topics.filter((t) => !subjectFilter || t.subjectIds.includes(subjectFilter)),
    [topics, subjectFilter],
  );

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  // The backend has no sort parameter, so sorting only reorders the current page.
  const sortedData = useMemo(() => {
    const col = allColumns.find((c) => c.key === orderBy);
    const valueOf = (row) => {
      if (orderBy === "duration") return row.durationSeconds;
      if (orderBy === "status") return row.active ? 1 : 0;
      if (orderBy === "recommended") return row.recommended ? 1 : 0;
      return col ? col.getValue(row) : row[orderBy];
    };
    return [...videos].sort((a, b) => {
      const aValue = valueOf(a);
      const bValue = valueOf(b);
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return order === "asc" ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
    });
  }, [videos, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, subjectFilter, topicFilter, statusFilter, recommendedFilter]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleSave = async (values) => {
    const { id, file, onProgress, ...fields } = values;
    try {
      const payload = { ...fields };
      if (file) {
        const [durationSeconds, videoPath] = await Promise.all([
          readVideoDurationSeconds(file),
          uploadVideoFile(file, fields.topicId, onProgress),
        ]);
        payload.videoPath = videoPath;
        if (durationSeconds != null) payload.durationSeconds = durationSeconds;
      }
      if (id) await updateVideo(id, payload);
      else await createVideo(payload);
      toast.success(id ? "Video updated successfully!" : "Video uploaded successfully!");
      setFormOpen(false);
      loadVideos();
    } catch (err) {
      toast.error(apiErrorMessage(err, id ? "Failed to update video" : "Failed to upload video"));
    }
  };

  const handleDelete = () => {
    deleteVideo(deleteId)
      .then(() => {
        toast.success("Video deleted!");
        setDeleteId(null);
        // Step back a page if that was the last row on this one.
        if (videos.length === 1 && page > 0) setPage(page - 1);
        else loadVideos();
      })
      .catch((err) => {
        setDeleteId(null);
        toast.error(apiErrorMessage(err, "Failed to delete video"));
      });
  };

  const hierarchyForForm = { courses, subjects, chapters, topics };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 620px", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Subject</InputLabel>
                <Select
                  label="Subject"
                  value={subjectFilter}
                  onChange={(e) => {
                    setSubjectFilter(e.target.value);
                    setTopicFilter("");
                  }}
                >
                  <MenuItem value="">All Subjects</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Topic</InputLabel>
                <Select label="Topic" value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)}>
                  <MenuItem value="">All Topics</MenuItem>
                  {filterTopicOptions.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Recommended</InputLabel>
                <Select label="Recommended" value={recommendedFilter} onChange={(e) => setRecommendedFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="YES">Recommended</MenuItem>
                  <MenuItem value="NO">Not recommended</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                {totalElements} videos
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 200, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <Button
                startIcon={<UploadFileIcon />}
                onClick={openAdd}
                variant="contained"
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Upload Video
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 280px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? order : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        color: "#ffffff !important",
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "110px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No videos found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          const value = c.getValue(row);
                          if (c.key === "id") {
                            return <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{value}</Typography>;
                          }
                          if (c.key === "title") {
                            return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{value}</Typography>;
                          }
                          if (c.key === "recommended") {
                            return value ? (
                              <Chip
                                label="Recommended"
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: "0.7rem", color: ACCENT, borderColor: ACCENT }}
                              />
                            ) : (
                              "—"
                            );
                          }
                          if (c.key === "status") return <StatusChip value={value} />;
                          return value;
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title={row.videoPath ? "Play Video" : "No preview available"} arrow placement="top">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={!row.videoPath}
                              onClick={() => setWatchItem(row)}
                              sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                            >
                              <PlayCircleFilledIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit Video" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Video" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(row.id)}
                            sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <VideoFormDialog
        open={formOpen}
        item={editing}
        hierarchy={hierarchyForForm}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <Dialog
        open={Boolean(watchItem)}
        onClose={() => setWatchItem(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            backgroundColor: ACCENT,
            color: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <OndemandVideoIcon fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
              {watchItem?.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setWatchItem(null)} sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, backgroundColor: "#000" }}>
          {watchItem?.videoPath && (
            <video
              key={watchItem.id}
              src={resolveMediaUrl(watchItem.videoPath)}
              controls
              autoPlay
              style={{ width: "100%", maxHeight: "70vh", display: "block" }}
            />
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this video? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentVideos;
